import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { useNavigate } from "@entry/navigation";

import { TextInput } from "@components/Input";
import { OnboardingLayoutProps } from "@components/OnboardingLayout";
import { Routes } from "@ctypes";
import { EntryStepLayout } from "@entry/EntryStepLayout";
import { useErrorBanner, useInitiateResetPassword } from "@hooks";
import { i18n, Translations } from "@i18n";
import { constants } from "@layout";
import { validationSchema } from "./content";
import { ForgotPasswordFormValues } from "./types";

export const ForgotPasswordRoute = () => {
  const {
    register,
    setValue,
    watch,
    handleSubmit: handleFormSubmit,
    formState: { errors, isValid },
  } = useForm<ForgotPasswordFormValues>({
    defaultValues: {
      email: "",
    },
    mode: "all",
    resolver: yupResolver(validationSchema),
  });

  const [initiateResetPassword, { loading }] = useInitiateResetPassword();
  const { showErrorBanner } = useErrorBanner();
  const navigate = useNavigate();

  const emailController = register("email");

  const onChangeEmailText = (value: string) => {
    setValue("email", value, constants.form);
  };

  const handleSubmit = async ({ email }: ForgotPasswordFormValues) => {
    try {
      const { data } = await initiateResetPassword({ variables: { email } });

      if (data?.initiateResetPassword) {
        navigate(Routes.ForgotPasswordConfirmation, { replace: true });
      } else {
        showErrorBanner();
      }
    } catch {
      showErrorBanner();
    }
  };

  const props: OnboardingLayoutProps = {
    title: i18n.t(Translations.ForgotPasswordRouteTitle),
    subtitle: i18n.t(Translations.ForgotPasswordRouteSubtitle),
    cta: {
      primary: {
        title: i18n.t(Translations.ForgotPasswordRoutePrimaryCTA),
        onClick: handleFormSubmit(handleSubmit),
        disabled: !isValid,
        loading,
      },
    },
  };

  return (
    <EntryStepLayout {...props}>
      <TextInput
        {...emailController}
        label={i18n.t(Translations.ForgotPasswordRouteEmailLabel)}
        placeholder={i18n.t(Translations.ForgotPasswordRouteEmailPlaceholder)}
        value={watch("email")}
        onChangeText={onChangeEmailText}
        error={errors.email?.message}
      />
    </EntryStepLayout>
  );
};
